<template>
  <Modal
    v-if="selectedProgramRef && userAccountRef"
    :on-exit="handleModalExit"
    :no-exit-on-backdrop="isMandatoryRef"
    :modal="mentorMatchingModalRef"
    :steps="modalStepsRef"
  />
</template>

<script setup lang="ts">
import { computed, watchEffect } from 'vue'

import useAccount from '@/features/auth/hooks/use-account.hook'
import { RoleEnumTypeV2 } from '@/features/auth/types'
import useGroups from '@/features/groups/hooks/use-groups.hook'
import MatchingModalContent from '@/features/matching/components/matching-modals/matching-modal-content/matching-modal-content.vue'
import useMatchingInformation from '@/features/matching/hooks/use-matching-information.hook'
import useMatchingSettings from '@/features/matching/hooks/use-matching-settings.hook'
import useProgram from '@/features/programs/hooks/use-program.hook'
import { selectGetSelectedProgramId } from '@/features/programs/stores/programs/programs.selectors'
import programsStore from '@/features/programs/stores/programs/programs.store'
import Modal from '@/features/ui/components/modal/modal.vue'
import {
  selectGetMentorMatchingModal,
  selectGetShouldHideMatchingModals,
} from '@/features/ui/store/modal/modal.selectors'
import modalsStore from '@/features/ui/store/modal/modal.store'
import useProgramRoles from '@/features/users/hooks/use-program-roles.hook'
import useUser from '@/features/users/hooks/use-user.hook'
import { selectGetCurrentRoute } from '@/store/route/route.selector'
import routeStore from '@/store/route/route.store'

const { accountRef } = useAccount()
const accountIdRef = computed(() => accountRef.value?.id)
const { userRef: userAccountRef } = useUser(accountIdRef)
const mentorMatchingModalRef = selectGetMentorMatchingModal(modalsStore)

const selectedProgramIdRef = selectGetSelectedProgramId(programsStore)
const { programRef: selectedProgramRef, isPendingRef: isProgramPendingRef } = useProgram(selectedProgramIdRef)
const { canFillCriteria } = useMatchingSettings(selectedProgramIdRef)
const { groupsRef, isPendingRef: isGroupsPendingRef } = useGroups()
const { programRolesRef } = useProgramRoles()

const currentRouteRef = selectGetCurrentRoute(routeStore)

const shouldHideMatchingModalsRef = selectGetShouldHideMatchingModals(modalsStore)

// Critères mentor : « remplis » seulement si tous les champs du template le sont
const { isMentorCriteriaCompleteRef: hasMatchingCriteriaRef, isStaffRef } = useMatchingInformation({
  onSubmitCallback: () => null,
})

// Un mentor ne lance jamais le matching lui-même : la saisie est obligatoire et
// la modale n'a pas de sortie (ni fond, ni Échap, ni « Remplir plus tard »).
// Exception : admin, manager ou support du programme, qui gardent la popup
// historique (dismissable, « Remplir plus tard »).
const isMandatoryRef = computed(() => !isStaffRef.value)

// Gérer la fermeture du modal.
// En saisie obligatoire, la seule fermeture possible est celle forcée par le
// composant Modal sur changement de route, et le watcher ci-dessous la rouvre
// aussitôt : on ne pose jamais le drapeau « masquer les modales de matching ».
const handleModalExit = () => {
  if (isMandatoryRef.value) return
  // Staff : comportement historique, « Remplir plus tard » ou fermeture sans
  // remplir masque les modales de matching pour la session.
  if (!hasMatchingCriteriaRef.value) {
    modalsStore.toggleShouldHideMatchingModals(true)
  }
}

// Déterminer les steps du modal selon les options de matching.
// La modale s'ouvre directement sur les champs : l'ancienne étape « rappel
// d'objectif » (description du programme + « Remplir plus tard ») a été fondue
// dans l'écran des critères.
const modalStepsRef = computed(() => {
  if (!canFillCriteria.value) {
    return []
  }

  // Critères déjà remplis → pas de steps (l'alert du dashboard gère l'info)
  if (hasMatchingCriteriaRef.value) {
    return []
  }

  // Critères pas encore remplis → permettre de les remplir
  return [{ component: MatchingModalContent, id: 'fill-criteria', stepName: null }]
})

// on mount, if it's a mentor, check if they have a mentorship group for the current program
// if the mentor doesn't have a mentorship group for the current program, show the mentor matching modal
watchEffect(() => {
  if (
    programRolesRef.value?.includes(RoleEnumTypeV2.MENTOR) &&
    !programRolesRef.value?.includes(RoleEnumTypeV2.MENTEE)
  ) {
    // Si les critères ne sont pas activés, ne jamais ouvrir le modal
    if (!canFillCriteria.value) {
      modalsStore.toggleModal('mentor-matching', false)
      return
    }

    // if it's pending or the user manually ask for the modal to be closed, close the modal
    if (isGroupsPendingRef.value || isProgramPendingRef.value || shouldHideMatchingModalsRef.value) {
      modalsStore.toggleModal('mentor-matching', false)
      return
    }

    // Check if we're on an admin path - if so, don't open the matching modal
    if (currentRouteRef.value?.path?.startsWith('/admin/')) {
      return
    }

    // Si les critères sont déjà remplis, ne pas ouvrir le modal
    if (hasMatchingCriteriaRef.value) {
      modalsStore.toggleModal('mentor-matching', false)
      return
    }

    if (!mentorMatchingModalRef.value.isOpen && groupsRef.value.length === 0) {
      modalsStore.toggleModal('mentor-matching', true)
    } else if (groupsRef.value.length > 0 && mentorMatchingModalRef.value.isOpen) {
      modalsStore.toggleModal('mentor-matching', false)
    }
  }
})
</script>
